<template>
  <div class="faq">
    <header class="faq__head">
      <button class="faq__back" type="button" :aria-label="t('vitrine.faq.back')" @click="emit('close')">‹</button>
      <h2 class="faq__title">{{ t('vitrine.faq.title') }}</h2>
    </header>

    <div class="faq__scroll">
      <div v-if="loading" class="faq__loading"><div class="spinner" /></div>
      <p v-else-if="error" class="faq__error">{{ t('vitrine.faq.loadError') }}</p>

      <template v-else>
        <input
          v-model="query"
          class="faq__search"
          type="search"
          :placeholder="t('vitrine.faq.search')"
        />

        <!-- Документы группами (сверху, §13 в.7) -->
        <section v-for="group in visibleGroups" :key="group.setName" class="faq__group">
          <h3 class="faq__group-name">{{ group.setName }}</h3>
          <div v-for="doc in group.documents" :key="doc.id" class="faq__doc">
            <template v-if="doc.kind === 'PDF'">
              <button class="faq__doc-pdf" type="button" @click="openPdf(doc)">
                <span class="faq__doc-title">{{ docTitle(doc) }}</span>
                <span class="faq__doc-hint">PDF ›</span>
              </button>
              <p v-if="docDesc(doc)" class="faq__doc-desc">{{ docDesc(doc) }}</p>
            </template>
            <details v-else class="faq__doc-text" @toggle="onToggle">
              <summary class="faq__doc-title">{{ docTitle(doc) }}</summary>
              <!-- eslint-disable-next-line vue/no-v-html -- HTML санитайзится renderMarkdown (escape-first) -->
              <div class="faq__md" v-html="docTextHtml(doc)" />
            </details>
          </div>
        </section>

        <!-- Q&A по категориям (ниже) -->
        <section v-for="cat in visibleCategories" :key="cat.key" class="faq__cat">
          <h3 class="faq__cat-name">{{ cat.title }}</h3>
          <details v-for="entry in cat.entries" :key="entry.id" class="faq__qa" @toggle="onToggle">
            <summary class="faq__q">{{ entry.question }}</summary>
            <!-- eslint-disable-next-line vue/no-v-html -- HTML санитайзится renderMarkdown (escape-first) -->
            <div class="faq__md" v-html="entry.answerHtml" />
          </details>
        </section>

        <p v-if="!hasAnything" class="faq__empty">{{ t('vitrine.faq.empty') }}</p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ensureFaqLoaded, faqData, pick, pickStrict, openFaqPdf, faqImageUrl, deferFaqImages,
  type DocumentItem,
} from '@/api/faq';
import { renderMarkdown } from '@/utils/markdown';

const emit = defineEmits<{ close: [] }>();
const { t, locale } = useI18n();

const page = faqData();
const loading = ref(true);
const error = ref(false);
const query = ref('');

onMounted(async () => {
  try {
    await ensureFaqLoaded();
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
});

const lang = computed(() => locale.value);
const needle = computed(() => query.value.trim().toLowerCase());

function matches(...parts: (string | null | undefined)[]): boolean {
  if (!needle.value) return true;
  return parts.some(p => p && p.toLowerCase().includes(needle.value));
}

function docTitle(doc: DocumentItem): string {
  return pick(doc.title, lang.value) ?? '';
}
function docDesc(doc: DocumentItem): string | null {
  return pick(doc.description, lang.value);
}
function docTextHtml(doc: DocumentItem): string {
  return deferFaqImages(renderMarkdown(pick(doc.textBody, lang.value)));
}

/** Видимые группы документов: фильтр по поиску (имя набора + название + текст), пустые убираем. */
const visibleGroups = computed(() => {
  const p = page.value;
  if (!p || !p.docsAvailable) return [];
  return p.documentGroups
    .map(g => ({
      setName: g.setName,
      documents: g.documents.filter(d =>
        matches(g.setName, pick(d.title, lang.value), pick(d.textBody, lang.value))),
    }))
    .filter(g => g.documents.length > 0);
});

/** Видимые категории Q&A: запись видна только если есть перевод вопроса на язык клиента (§10). */
const visibleCategories = computed(() => {
  const p = page.value;
  if (!p) return [];
  const result: { key: string; title: string; entries: { id: string; question: string; answerHtml: string }[] }[] = [];
  for (const cat of p.faqCategories) {
    const catTitle = cat.id === null ? t('vitrine.faq.misc') : pickStrict(cat.name, lang.value);
    if (!catTitle) continue;  // категория без перевода на язык клиента — скрываем
    const entries = cat.entries
      .map(e => {
        const question = pickStrict(e.question, lang.value);
        if (!question) return null;  // запись без перевода вопроса — скрываем
        const answerRaw = pickStrict(e.answer, lang.value) ?? pick(e.answer, lang.value);
        if (!matches(question, answerRaw)) return null;
        return { id: e.id, question, answerHtml: deferFaqImages(renderMarkdown(answerRaw)) };
      })
      .filter((e): e is { id: string; question: string; answerHtml: string } => e !== null);
    if (entries.length > 0) {
      result.push({ key: cat.id ?? '__misc__', title: catTitle, entries });
    }
  }
  return result;
});

const hasAnything = computed(() => visibleGroups.value.length > 0 || visibleCategories.value.length > 0);

async function openPdf(doc: DocumentItem) {
  await openFaqPdf(doc.id, lang.value);
}

/** При раскрытии <details> подменяем `data-faq-img` реальным blob-src (ленивая загрузка картинок). */
function onToggle(e: Event) {
  const el = e.target as HTMLDetailsElement;
  if (!el.open) return;
  el.querySelectorAll<HTMLImageElement>('img[data-faq-img]').forEach(async img => {
    const id = img.getAttribute('data-faq-img');
    if (!id) return;
    img.removeAttribute('data-faq-img');  // одноразово
    try {
      img.src = await faqImageUrl(id);
    } catch {
      /* картинка недоступна — остаётся без src */
    }
  });
}
</script>

<style scoped lang="scss">
.faq { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.faq__head {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 14px; border-bottom: 1px solid var(--hairline);
}
.faq__back {
  background: none; border: none; color: var(--gold);
  font-size: 26px; line-height: 1; cursor: pointer; padding: 0 6px;
}
.faq__title { font-size: 16px; margin: 0; color: var(--text); }
.faq__scroll { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 14px; }

.faq__loading { display: grid; place-items: center; padding: 40px 0; }
.spinner {
  width: 26px; height: 26px;
  border: 3px solid var(--gold); border-top-color: transparent;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.faq__error, .faq__empty { color: var(--text-3); font-size: 13px; margin: 8px 0; }

.faq__search {
  width: 100%; border-radius: 12px;
  background: var(--bg-card); border: 1px solid var(--hairline);
  color: var(--text); padding: 10px 12px; font-size: 14px; font-family: inherit;
}

.faq__group, .faq__cat { display: flex; flex-direction: column; gap: 8px; }
.faq__group-name, .faq__cat-name {
  font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--text-3); margin: 4px 0 0;
}

.faq__doc { display: flex; flex-direction: column; gap: 2px; }
.faq__doc-pdf {
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  background: var(--bg-card); border: 1px solid var(--hairline); border-radius: 12px;
  color: var(--text); padding: 12px 14px; cursor: pointer; font: inherit; text-align: left;
}
.faq__doc-hint { color: var(--gold); font-size: 12px; white-space: nowrap; }
.faq__doc-desc { color: var(--text-3); font-size: 12px; margin: 2px 4px 0; }

.faq__doc-text, .faq__qa {
  background: var(--bg-card); border: 1px solid var(--hairline); border-radius: 12px;
  padding: 0 14px;
}
.faq__doc-title, .faq__q {
  cursor: pointer; padding: 12px 0; color: var(--text); font-size: 14px;
  list-style: none; font-weight: 500;
  &::-webkit-details-marker { display: none; }
}
.faq__md {
  color: var(--text); font-size: 14px; line-height: 1.5; padding: 0 0 12px;
  :deep(img) { max-width: 100%; border-radius: 8px; }
  :deep(a) { color: var(--gold); }
  :deep(h1), :deep(h2), :deep(h3) { font-size: 15px; margin: 8px 0 4px; }
  :deep(ul), :deep(ol) { padding-left: 20px; margin: 4px 0; }
  // Markdown spoiler blocks (`+++ Title` … `+++`) — collapsible troubleshooting inside an answer.
  :deep(details) { margin: 8px 0; border: 1px solid var(--hairline); border-radius: 10px; padding: 0 12px; }
  :deep(details > summary) {
    cursor: pointer; padding: 10px 0; color: var(--gold); font-weight: 500; list-style: none;
    &::-webkit-details-marker { display: none; }
    &::after { content: '›'; float: right; transition: transform 0.15s; }
  }
  :deep(details[open] > summary)::after { transform: rotate(90deg); }
}
</style>
